import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Drawer,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSelector } from "react-redux";
import { createXPortfolioDistribution, createXPortfolioInvestment, getXPortfolioHealth, getXPortfolioState } from "../../util/API/xportfolio";
import XPortfolioTab from "./XPortfolioTabs";
import InvestorLibrary from "./InvestorLibrary";
import "./XPortfolio.scss";

const moduleTabs = [
  "Overview",
  "Funds",
  "Marketplace",
  "Investments",
  "Applications",
  "Documents",
  "Announcements",
  "Administration",
];

const currency = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });
const formatMoney = (value) => currency.format(Number(value) || 0);
const formatMultiple = (value) => value == null ? "—" : `${value.toFixed(2)}x`;
const formatPercent = (value) => value == null ? "N/A" : `${value.toFixed(1)}%`;
const formatAnnouncementDate = (value) => value
  ? new Intl.DateTimeFormat("en-CA", { month: "short", day: "numeric" }).format(new Date(value))
  : "Unscheduled";

function normalizePortfolio(stateResult = {}) {
  return {
    ...stateResult,
    investments: stateResult.investments || [], funds: stateResult.funds || [], applications: stateResult.applications || [],
    documents: stateResult.documents || [], announcements: stateResult.announcements || [], users: stateResult.users || [],
    entities: stateResult.entities || [], roles: stateResult.roles || [], documentFolders: stateResult.documentFolders || [],
    auditLogs: stateResult.auditLogs || [], investors: stateResult.investors || [],
    authorityDelegations: stateResult.authorityDelegations || [],
  };
}

function latestValuation(investment) {
  const valuations = [...(investment.valuations || [])].sort((a, b) => new Date(b.date) - new Date(a.date));
  return Number(valuations[0]?.amount ?? investment.amount) || 0;
}

function portfolioMetrics(investments) {
  const invested = investments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const distributions = investments.reduce(
    (sum, item) => sum + (item.distributions || []).reduce((total, flow) => total + Number(flow.amount || 0), 0),
    0
  );
  const remainingValue = investments.reduce((sum, item) => sum + latestValuation(item), 0);
  const totalValue = distributions + remainingValue;
  const tvpi = invested > 0 ? totalValue / invested : null;
  const dpi = invested > 0 ? distributions / invested : null;
  const earliestDate = investments.map((item) => item.date).filter(Boolean).sort()[0];
  const years = earliestDate ? Math.max(0, (Date.now() - new Date(earliestDate).getTime()) / 31557600000) : 0;
  const roi = invested > 0 && years >= 1 / 365.25 && totalValue >= 0
    ? (Math.pow(totalValue / invested, 1 / years) - 1) * 100
    : null;
  return { invested, distributions, remainingValue, totalValue, tvpi, dpi, roi, years };
}

function performanceSeries(investments) {
  const dated = new Set();
  investments.forEach((investment) => {
    if (investment.date) dated.add(investment.date);
    (investment.distributions || []).forEach((item) => item.date && dated.add(item.date));
    (investment.valuations || []).forEach((item) => item.date && dated.add(item.date));
  });
  if (!dated.size) return [];
  dated.add(new Date().toISOString().slice(0, 10));
  return [...dated].sort().map((pointDate) => {
    let invested = 0;
    let distributions = 0;
    let remaining = 0;
    investments.forEach((investment) => {
      if (!investment.date || investment.date > pointDate) return;
      invested += Number(investment.amount || 0);
      distributions += (investment.distributions || []).filter((item) => item.date <= pointDate).reduce((sum, item) => sum + Number(item.amount || 0), 0);
      const valuation = [...(investment.valuations || [])].filter((item) => item.date <= pointDate).sort((a, b) => new Date(b.date) - new Date(a.date))[0];
      remaining += Number(valuation?.amount ?? investment.amount) || 0;
    });
    return { date: pointDate, invested, total: distributions + remaining };
  });
}

function smoothSvgPath(points) {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0][0]} ${points[0][1]}`;
  return points.slice(0, -1).reduce((path, point, index) => {
    const previous = points[index - 1] || point;
    const next = points[index + 1];
    const following = points[index + 2] || next;
    const controlOne = [point[0] + (next[0] - previous[0]) / 6, point[1] + (next[1] - previous[1]) / 6];
    const controlTwo = [next[0] - (following[0] - point[0]) / 6, next[1] - (following[1] - point[1]) / 6];
    return `${path} C ${controlOne[0]} ${controlOne[1]}, ${controlTwo[0]} ${controlTwo[1]}, ${next[0]} ${next[1]}`;
  }, `M ${points[0][0]} ${points[0][1]}`);
}

function PortfolioPerformanceChart({ investments }) {
  const series = useMemo(() => performanceSeries(investments), [investments]);
  if (!series.length) return <div className="xportfolio-chart-empty">Performance history will appear after the first investment is recorded.</div>;
  const width = 1000;
  const height = 280;
  const left = 68;
  const right = 20;
  const top = 18;
  const bottom = 38;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const maximum = Math.max(1, ...series.flatMap((item) => [item.total, item.invested]));
  const x = (index) => left + (series.length === 1 ? chartWidth / 2 : (index / (series.length - 1)) * chartWidth);
  const y = (value) => top + chartHeight - (value / maximum) * chartHeight;
  const totalCoordinates = series.map((item, index) => [x(index), y(item.total)]);
  const investedCoordinates = series.map((item, index) => [x(index), y(item.invested)]);
  const totalPath = smoothSvgPath(totalCoordinates);
  const investedPath = smoothSvgPath(investedCoordinates);
  const areaPath = `${totalPath} L ${totalCoordinates[totalCoordinates.length - 1][0]} ${top + chartHeight} L ${totalCoordinates[0][0]} ${top + chartHeight} Z`;
  const formatAxis = (value) => value >= 1000000 ? `$${(value / 1000000).toFixed(1)}m` : value >= 1000 ? `$${Math.round(value / 1000)}k` : `$${Math.round(value)}`;
  const dateLabel = (value) => new Intl.DateTimeFormat("en-CA", { month: "short", year: "2-digit" }).format(new Date(`${value}T12:00:00`));
  const labelIndexes = [...new Set([0, Math.floor((series.length - 1) / 2), series.length - 1])];
  return <div className="xportfolio-chart-real">
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Portfolio total value and invested capital over time">
      <defs><linearGradient id="xpValueFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#c8f04b" stopOpacity=".35" /><stop offset="100%" stopColor="#c8f04b" stopOpacity="0" /></linearGradient></defs>
      {[0, .25, .5, .75, 1].map((ratio) => <g key={ratio}><line x1={left} x2={width - right} y1={top + chartHeight * ratio} y2={top + chartHeight * ratio} className="xp-chart-gridline" /><text x={left - 10} y={top + chartHeight * ratio + 4} textAnchor="end">{formatAxis(maximum * (1 - ratio))}</text></g>)}
      <path d={areaPath} fill="url(#xpValueFill)" />
      <path d={investedPath} className="xp-chart-invested-line" />
      <path d={totalPath} className="xp-chart-total-line" />
      {series.map((item, index) => <g key={item.date} className="xp-chart-point"><circle cx={x(index)} cy={y(item.total)} r="5"><title>{`${dateLabel(item.date)} — Total value ${formatMoney(item.total)}; Invested ${formatMoney(item.invested)}`}</title></circle></g>)}
      {labelIndexes.map((index) => <text key={index} x={x(index)} y={height - 10} textAnchor={index === 0 ? "start" : index === series.length - 1 ? "end" : "middle"}>{dateLabel(series[index].date)}</text>)}
    </svg>
  </div>;
}

function AddInvestmentDrawer({ open, onClose, portfolio, role, onCreated }) {
  const [form, setForm] = useState({ investorId: "", fundId: "", opportunity: "", investmentDate: new Date().toISOString().slice(0, 10), amount: "", adminFee: "0", tax: "0", managementFee: "2", performanceFee: "20" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const update = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const chooseFund = (event) => {
    const fund = portfolio.funds.find((item) => String(item.id) === event.target.value);
    setForm((current) => ({ ...current, fundId: event.target.value, opportunity: fund?.name || current.opportunity }));
  };
  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true); setError("");
    try {
      const result = await createXPortfolioInvestment(form);
      await onCreated(result.message || "Investment added successfully.");
      setForm((current) => ({ ...current, fundId: "", opportunity: "", amount: "", adminFee: "0", tax: "0" }));
      onClose();
    } catch (requestError) {
      setError(requestError.message);
    } finally { setSubmitting(false); }
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ className: "xp-investment-drawer" }} ModalProps={{ keepMounted: true }}>
    <form onSubmit={submit}>
      <Box className="xp-drawer-heading"><Box><span>Portfolio record</span><Typography component="h2">Add investment</Typography><p>Capture a commitment and its investor-facing fees.</p></Box><button type="button" onClick={onClose}>×</button></Box>
      {error && <Alert severity="error">{error}</Alert>}
      {role === "Administrator" && <TextField select label="Investor" value={form.investorId} onChange={update("investorId")} required>{portfolio.investors.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}</TextField>}
      <TextField select label="Related fund" value={form.fundId} onChange={chooseFund}><MenuItem value="">No linked fund</MenuItem>{portfolio.funds.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}</TextField>
      <TextField label="Opportunity name" value={form.opportunity} onChange={update("opportunity")} required />
      <div className="xp-drawer-two"><TextField label="Investment date" type="date" value={form.investmentDate} onChange={update("investmentDate")} required InputLabelProps={{ shrink: true }} /><TextField label="Investment amount" type="number" value={form.amount} onChange={update("amount")} required inputProps={{ min: 0.01, step: 0.01 }} /></div>
      <div className="xp-drawer-two"><TextField label="Admin / upfront fee" type="number" value={form.adminFee} onChange={update("adminFee")} inputProps={{ min: 0, step: 0.01 }} /><TextField label="Associated tax" type="number" value={form.tax} onChange={update("tax")} inputProps={{ min: 0, step: 0.01 }} /></div>
      <div className="xp-drawer-two"><TextField label="Management fee (%)" type="number" value={form.managementFee} onChange={update("managementFee")} inputProps={{ min: 0, step: 0.01 }} /><TextField label="Performance fee (%)" type="number" value={form.performanceFee} onChange={update("performanceFee")} inputProps={{ min: 0, step: 0.01 }} /></div>
      <div className="xp-invoice-total"><span>Investor invoice total<small>Investment + upfront fee + tax</small></span><strong>{formatMoney(Number(form.amount) + Number(form.adminFee) + Number(form.tax))}</strong></div>
      <div className="xp-drawer-actions"><Button onClick={onClose} className="xportfolio-secondary-action">Cancel</Button><Button type="submit" disabled={submitting} className="xportfolio-primary-action">{submitting ? "Saving…" : "Save investment"}</Button></div>
    </form>
  </Drawer>;
}

function RecordDistributionDialog({ open, onClose, investments, preferredInvestmentId, onCreated }) {
  const [form, setForm] = useState({ investmentId: "", distributionDate: new Date().toISOString().slice(0, 10), amount: "", paymentMethod: "Bank transfer" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const preferred = preferredInvestmentId !== "all" && investments.some((item) => String(item.id) === String(preferredInvestmentId))
      ? String(preferredInvestmentId)
      : String(investments[0]?.id || "");
    setForm((current) => ({ ...current, investmentId: preferred, amount: "", distributionDate: new Date().toISOString().slice(0, 10) }));
    setError("");
  }, [open, preferredInvestmentId, investments]);

  const update = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const result = await createXPortfolioDistribution(form);
      await onCreated(result.message || "Distribution recorded successfully.");
      onClose();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  return <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ className: "xp-distribution-dialog" }}>
    <form onSubmit={submit}>
      <Box className="xp-drawer-heading">
        <Box><span>Cash flow</span><Typography component="h2">Record distribution</Typography><p>Add a payment received from an opportunity.</p></Box>
        <button type="button" onClick={onClose} aria-label="Close distribution form">×</button>
      </Box>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField select label="Investment" value={form.investmentId} onChange={update("investmentId")} required fullWidth>
          {investments.map((item) => <MenuItem key={item.id} value={item.id}>{item.opportunity}</MenuItem>)}
        </TextField>
        <div className="xp-drawer-two">
          <TextField label="Distribution date" type="date" value={form.distributionDate} onChange={update("distributionDate")} required InputLabelProps={{ shrink: true }} />
          <TextField label="Amount" type="number" value={form.amount} onChange={update("amount")} required inputProps={{ min: 0.01, step: 0.01 }} />
        </div>
        <TextField select label="Payment method" value={form.paymentMethod} onChange={update("paymentMethod")} required fullWidth>
          <MenuItem value="Bank transfer">Bank transfer</MenuItem>
          <MenuItem value="Wire transfer">Wire transfer</MenuItem>
          <MenuItem value="Cheque">Cheque</MenuItem>
          <MenuItem value="Other">Other</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions className="xp-dialog-actions">
        <Button onClick={onClose} disabled={submitting} className="xportfolio-secondary-action">Cancel</Button>
        <Button type="submit" disabled={submitting || !investments.length} className="xportfolio-primary-action">{submitting ? "Recording…" : "Record distribution"}</Button>
      </DialogActions>
    </form>
  </Dialog>;
}

function roleFromPortal(configVariables) {
  const storedUser = sessionStorage.getItem("user");
  let portalUser = null;
  try {
    portalUser = storedUser ? JSON.parse(storedUser) : null;
  } catch {
    portalUser = null;
  }
  const roles = portalUser?.payload?.body?.Securityuserrole;
  const roleList = Array.isArray(roles) ? roles : roles ? [roles] : [];
  const parseIds = (value) =>
    value?.toString().split(",").map((id) => Number(id.trim())).filter(Boolean) || [];
  const adminIds = parseIds(configVariables?.portfolioAdminRoleIds);
  const delegateIds = parseIds(configVariables?.delegateRoleIds);

  if (sessionStorage.getItem("isAdmin") === "true" || roleList.some((role) => adminIds.includes(Number(role?.roleid)))) {
    return "Administrator";
  }
  if (roleList.some((role) => delegateIds.includes(Number(role?.roleid)))) return "Delegate";
  return "Investor";
}

export default function XPortfolio() {
  const configVariables = useSelector((state) => state.config.baseConfig);
  const portalUser = useSelector((state) => state.login.user);
  const [activeTab, setActiveTab] = useState(0);
  const [portfolioFilter, setPortfolioFilter] = useState(() => {
    try { return window.localStorage.getItem("paiy.xportfolio.portfolioFilter") || "all"; }
    catch { return "all"; }
  });
  const [investmentDrawerOpen, setInvestmentDrawerOpen] = useState(false);
  const [distributionDialogOpen, setDistributionDialogOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [health, setHealth] = useState({ loading: true, connected: false, message: "Checking service…" });
  const [portfolio, setPortfolio] = useState(normalizePortfolio());
  const role = useMemo(() => roleFromPortal(configVariables), [configVariables]);
  const displayName = portalUser?.firstName || portalUser?.firstname || portalUser?.name?.split(" ")?.[0] || "Investor";
  const visibleInvestments = useMemo(
    () => portfolioFilter === "all" ? portfolio.investments : portfolio.investments.filter((item) => String(item.id) === portfolioFilter),
    [portfolio.investments, portfolioFilter]
  );
  const metrics = useMemo(() => portfolioMetrics(visibleInvestments), [visibleInvestments]);
  const overviewAnnouncements = useMemo(() => {
    const now = Date.now();
    return (portfolio.announcements || [])
      .filter((item) => item.active !== false
        && String(item.status || "").toLowerCase() === "published"
        && (!item.expiresAt || new Date(item.expiresAt).getTime() >= now));
  }, [portfolio.announcements]);
  const distributionShare = metrics.totalValue > 0 ? (metrics.distributions / metrics.totalValue) * 100 : 0;
  const metricCards = useMemo(() => [
    { label: "Portfolio value", value: formatMoney(metrics.totalValue), hint: `${formatPercent(metrics.roi)} total return`, tone: "dark", math: `Total value = Distributions + Remaining value\n${formatMoney(metrics.distributions)} + ${formatMoney(metrics.remainingValue)} = ${formatMoney(metrics.totalValue)}` },
    { label: "TVPI", value: formatMultiple(metrics.tvpi), hint: "Total value ÷ paid-in capital", math: `TVPI = (Distributions + Remaining value) ÷ Paid-in capital\n(${formatMoney(metrics.distributions)} + ${formatMoney(metrics.remainingValue)}) ÷ ${formatMoney(metrics.invested)} = ${formatMultiple(metrics.tvpi)}` },
    { label: "DPI", value: formatMultiple(metrics.dpi), hint: "Distributions ÷ paid-in capital", math: `DPI = Distributions ÷ Paid-in capital\n${formatMoney(metrics.distributions)} ÷ ${formatMoney(metrics.invested)} = ${formatMultiple(metrics.dpi)}` },
    { label: "Annualized ROI", value: formatPercent(metrics.roi), hint: metrics.roi == null ? "Available after one day" : `Annualized over ${metrics.years.toFixed(1)} years`, tone: "lime", math: `Annualized ROI = [(Total value ÷ Invested capital)^(1 ÷ Years held) − 1] × 100\n[(${formatMoney(metrics.totalValue)} ÷ ${formatMoney(metrics.invested)})^(1 ÷ ${metrics.years.toFixed(2)}) − 1] × 100 = ${formatPercent(metrics.roi)}` },
  ], [metrics]);

  useEffect(() => {
    let active = true;
    Promise.all([getXPortfolioHealth(), getXPortfolioState()])
      .then(([healthResult, stateResult]) => {
        if (!active) return;
        setPortfolio(normalizePortfolio(stateResult));
        setHealth({
          loading: false,
          connected: healthResult?.status === "ok" && stateResult?.status === "ok",
          message: stateResult?.message || healthResult?.message || "xPortfolio service is available.",
        });
      })
      .catch((error) => {
        if (!active) return;
        setHealth({ loading: false, connected: false, message: error.message });
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    try { window.localStorage.setItem("paiy.xportfolio.portfolioFilter", portfolioFilter); }
    catch { /* Storage may be unavailable in privacy-restricted contexts. */ }
  }, [portfolioFilter]);

  useEffect(() => {
    if (portfolioFilter !== "all" && portfolio.investments.length && !portfolio.investments.some((item) => String(item.id) === String(portfolioFilter))) {
      setPortfolioFilter("all");
    }
  }, [portfolio.investments, portfolioFilter]);

  const refreshAfterInvestment = async (message) => {
    const stateResult = await getXPortfolioState();
    const refreshedPortfolio = normalizePortfolio(stateResult);
    setPortfolio(refreshedPortfolio);
    setPortfolioFilter((current) => current === "all" || refreshedPortfolio.investments.some((item) => String(item.id) === String(current)) ? current : "all");
    setToast(message);
  };

  return (
    <Box className="xportfolio-page page-container">
      <section className="xportfolio-hero">
        <Box>
          <span className="xportfolio-eyebrow">{new Intl.DateTimeFormat("en-CA", { weekday: "long", month: "long", day: "numeric" }).format(new Date())}</span>
          <Typography component="h1">Good day, {displayName}.</Typography>
          <Typography component="p">
            Here&apos;s how your private market portfolio is performing.
          </Typography>
        </Box>
        <Box className="xportfolio-hero-actions">
          <label className="xportfolio-filter"><span>Portfolio view</span><select value={portfolioFilter} onChange={(event) => setPortfolioFilter(event.target.value)}><option value="all">All opportunities</option>{portfolio.investments.map((investment) => <option key={investment.id} value={investment.id}>{investment.opportunity}</option>)}</select></label>
          <Button className="xportfolio-library-action" onClick={() => setLibraryOpen(true)}>Aa&nbsp; Investor Library</Button>
          <Chip label={role} className="xportfolio-role-chip" />
          <Button variant="contained" className="xportfolio-primary-action" onClick={() => setInvestmentDrawerOpen(true)}>+ Add investment</Button>
        </Box>
      </section>

      <Box className="xportfolio-tabs" role="navigation" aria-label="xPortfolio sections">
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} variant="scrollable" scrollButtons="auto">
          {moduleTabs.map((label) => <Tab key={label} label={label} />)}
        </Tabs>
      </Box>

      {!health.connected && <Alert
        severity={health.loading ? "info" : "warning"}
        icon={health.loading ? <CircularProgress size={18} /> : undefined}
        className="xportfolio-service-status"
      >
        <strong>Portfolio service:</strong> {health.message}
      </Alert>}

      {activeTab === 0 ? (
        <>
          <section className="xportfolio-metric-grid" aria-label="Portfolio metrics">
            <article className="xportfolio-metric-card xportfolio-announcement-card">
              <div className="xportfolio-announcement-card-heading">
                <span>Communications</span>
                <strong>Announcements</strong>
              </div>
              <div className="xportfolio-announcement-card-list">
                {overviewAnnouncements.map((item) => <button type="button" key={item.id} onClick={() => setActiveTab(moduleTabs.indexOf("Announcements"))}>
                  <strong>{item.title}</strong>
                  <small>{formatAnnouncementDate(item.publishedAt)}</small>
                </button>)}
                {!overviewAnnouncements.length && <small>No published announcements.</small>}
              </div>
              <Button className="xportfolio-announcement-card-action" onClick={() => setActiveTab(moduleTabs.indexOf("Announcements"))}>View all</Button>
            </article>
            {metricCards.map((metric) => (
              <article key={metric.label} className={`xportfolio-metric-card ${metric.tone || ""}`}>
                <span className="xportfolio-metric-label">{metric.label}<Tooltip title={<span className="xportfolio-math-tooltip">{metric.math}</span>} arrow placement="bottom"><button type="button" aria-label={`${metric.label} calculation`}><InfoOutlinedIcon /></button></Tooltip></span>
                <strong>{metric.value}</strong>
                <small>{metric.hint}</small>
              </article>
            ))}
          </section>

          <section className="xportfolio-workspace-grid">
            <article className="xportfolio-panel xportfolio-performance-panel">
              <Box className="xportfolio-panel-heading">
                <Box>
                  <span>Performance</span>
                  <Typography component="h2">Portfolio value</Typography>
                  <strong className="xportfolio-chart-value">{formatMoney(metrics.totalValue)}</strong>
                </Box>
                <div className="xportfolio-chart-legend"><span><i className="total" />Total value</span><span><i />Invested</span><InsightsOutlinedIcon /></div>
              </Box>
              <PortfolioPerformanceChart investments={visibleInvestments} />
              <p className="xportfolio-chart-summary">{visibleInvestments.length} investments · {formatMoney(metrics.invested)} paid-in capital</p>
            </article>

            <article className="xportfolio-panel">
              <Box className="xportfolio-panel-heading">
                <Box><span>Allocation</span><Typography component="h2">Value composition</Typography></Box>
              </Box>
              <div className="xportfolio-allocation">
                <div className="xportfolio-donut" style={{ background: `conic-gradient(var(--xp-lime) 0 ${distributionShare}%, var(--xp-ink) ${distributionShare}% 100%)` }}><div><strong>{formatMoney(metrics.totalValue)}</strong><small>Total value</small></div></div>
                <div className="xportfolio-allocation-row"><span><i className="distribution" />Distributions</span><strong>{formatMoney(metrics.distributions)}</strong></div>
                <div className="xportfolio-allocation-row"><span><i />Remaining value</span><strong>{formatMoney(metrics.remainingValue)}</strong></div>
              </div>
            </article>
          </section>

          <section className="xportfolio-panel xportfolio-investments-panel">
            <Box className="xportfolio-panel-heading">
              <Box><span>Portfolio</span><Typography component="h2">Your investments</Typography></Box>
              <Button className="xportfolio-secondary-action" onClick={() => setDistributionDialogOpen(true)} disabled={!visibleInvestments.length}>Record distribution</Button>
            </Box>
            <div className="xportfolio-investment-table">
              <div className="xportfolio-investment-head"><span>Opportunity</span><span>Invested</span><span>Distributions</span><span>Current value</span><span>TVPI</span><span>Status</span></div>
              {visibleInvestments.map((investment) => {
                const itemMetrics = portfolioMetrics([investment]);
                return <div className="xportfolio-investment-row" key={investment.id}>
                  <span><strong>{investment.opportunity}</strong><small>Since {investment.date}</small></span>
                  <span>{formatMoney(itemMetrics.invested)}</span>
                  <span>{formatMoney(itemMetrics.distributions)}</span>
                  <span>{formatMoney(itemMetrics.remainingValue)}</span>
                  <span><strong>{formatMultiple(itemMetrics.tvpi)}</strong></span>
                  <span><Chip size="small" label={investment.status || "Active"} /></span>
                </div>;
              })}
              {!visibleInvestments.length && <div className="xportfolio-empty-state">No investment records were returned for this account.</div>}
            </div>
          </section>

        </>
      ) : <XPortfolioTab tab={moduleTabs[activeTab]} data={portfolio} onAddInvestment={() => setInvestmentDrawerOpen(true)} onStateRefresh={refreshAfterInvestment} role={role} />}
      <InvestorLibrary open={libraryOpen} onClose={() => setLibraryOpen(false)} />
      <AddInvestmentDrawer open={investmentDrawerOpen} onClose={() => setInvestmentDrawerOpen(false)} portfolio={portfolio} role={role} onCreated={refreshAfterInvestment} />
      <RecordDistributionDialog open={distributionDialogOpen} onClose={() => setDistributionDialogOpen(false)} investments={visibleInvestments} preferredInvestmentId={portfolioFilter} onCreated={refreshAfterInvestment} />
      <Snackbar open={Boolean(toast)} autoHideDuration={4500} onClose={() => setToast("")} message={toast} />
    </Box>
  );
}
